import React, { useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import {
    ConcatFieldType,
    SelectedTargetFields,
    TargetFieldOptions,
} from "../types";
import useHoverAction from "../hooks/useHoverAction";
import DeleteButton from "../components/DeleteButton";
import ErrorHandler from "../components/ErrorHandler";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import ConcatValueField from "./ConcatValueField";
import AddButton from "../components/AddButton";
import { getDefaultValue, selectCustomStyle } from "../helpers/componentUtils";
import CustomOption from "../components/CustomOption";

type ConcatFieldProps = {
    field: ConcatFieldType;
    getUpdatedTargetFields: () => {
        targetFieldOptions: TargetFieldOptions;
        selectedTargetFields: SelectedTargetFields;
    };
    updateConcatFields: (key: string, value: string, id: string) => void;
    sourceFieldOptions: { label: string; value: string }[];
    removeConcatField: () => void;
    addField: (type: "field" | "separator") => void;
    updateField: (id: string, value: string) => void;
    removeField: (id: string) => void;
};

const Field = ({
    children,
    label,
}: {
    children: React.ReactNode;
    label: string;
}) => (
    <Grid
        container
        sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 0, 0, 2),
        }}
    >
        <Grid item xs={4} lg={2}>
            <Typography
                sx={{
                    fontWeight: 600,
                    color: theme.palette.gray[500],
                    fontSize: "14px",
                    width: "120px",
                }}
            >
                {label}
            </Typography>
        </Grid>
        <Grid item xs={8} lg={10}>
            {children}
        </Grid>
    </Grid>
);

const ConcatField: React.FC<ConcatFieldProps> = ({
    field,
    getUpdatedTargetFields,
    updateConcatFields,
    updateField,
    sourceFieldOptions,
    removeConcatField,
    addField,
    removeField,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    const { fields, hasError, targetFieldName, id } = field;

    const { targetFieldOptions, selectedTargetFields } =
        getUpdatedTargetFields();

    const defaultValue = getDefaultValue({
        id,
        selectedTargetFields,
        targetFieldOptions,
    });
    return (
        <>
            <ErrorHandler
                hasError={hasError}
                errorMessage={`Please fill in all fields to continue.`}
            />
            <Box
                ref={containerRef}
                {...handlers}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    gap: theme.spacing(2),
                    padding: theme.spacing(4, 0, 2, 0),
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.gray[200]}`,
                }}
            >
                <Field label="Target Field">
                    <AcxSelect
                        defaultValue={defaultValue}
                        optionComponent={CustomOption}
                        id={`condition-rule-${targetFieldName}-id`}
                        placeholder="Select Target Field"
                        options={targetFieldOptions.grouped}
                        error={hasError && !targetFieldName}
                        fullWidth
                        customStyle={selectCustomStyle({
                            maxWidth: `calc(100% - ${theme.spacing(4)})`,
                        })}
                        onChange={(e) => {
                            // The no selection option has an empty value
                            updateConcatFields(
                                "targetFieldName",
                                e.value === "" ? "" : e.label,
                                e.id,
                            );
                        }}
                    />
                </Field>
                {fields.map((field) => (
                    <Field
                        key={field.id}
                        label={
                            field.type === "field"
                                ? "Field Selection"
                                : "Separator Input"
                        }
                    >
                        <ConcatValueField
                            field={field}
                            hasError={hasError}
                            sourceFieldOptions={sourceFieldOptions}
                            updateField={(value) =>
                                updateField(field.id, value)
                            }
                            removeField={() => removeField(field.id)}
                        />
                    </Field>
                ))}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        paddingLeft: 2,
                        paddingTop: 1,
                    }}
                >
                    <AddButton onClick={() => addField("field")}>
                        Add field
                    </AddButton>
                    <AddButton onClick={() => addField("separator")}>
                        Add separator
                    </AddButton>
                </Box>
                {!!fields.length && (
                    <Box
                        sx={{
                            borderTop: `1px solid ${theme.palette.gray[200]}`,
                            padding: theme.spacing(2),
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "16px",
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            Preview
                        </Typography>
                        <Typography
                            sx={{ fontSize: "14px", overflowWrap: "anywhere" }}
                        >
                            {fields.map((field) => field.value)}
                        </Typography>
                    </Box>
                )}
                <DeleteButton
                    onDelete={removeConcatField}
                    showDelete={showAction}
                    sx={{
                        position: "absolute",
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                    }}
                />
            </Box>
        </>
    );
};

export default ConcatField;
